import { motion } from "framer-motion";
import { GraduationCap, Building2, UserRound, FileCheck2, BadgeCheck, MapPin, Rocket } from "lucide-react";
import { SectionHeading, Reveal } from "@/components/Reveal";
import { COMPANY, LOGO } from "@/data/content";
import { Separator } from "@/components/ui/separator";

export const AboutSection = () => {
  return (
    <section id="about" className="py-16 sm:py-20 lg:py-24" data-testid="about-section">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-5">
          <Reveal>
            <div
              className="relative h-[400px] sm:h-[480px] rounded-2xl bg-[#0d0e14] border border-border overflow-hidden grid place-items-center"
              data-testid="about-logo-visual"
            >
              <div
                aria-hidden="true"
                className="absolute inset-0 pointer-events-none"
                style={{
                  background:
                    "radial-gradient(420px circle at 50% 45%, rgba(214,179,106,0.10), transparent 65%)",
                }}
              />
              <motion.span
                aria-hidden="true"
                className="absolute w-72 h-72 rounded-full border border-dashed border-primary/25"
                animate={{ rotate: 360 }}
                transition={{ duration: 46, repeat: Infinity, ease: "linear" }}
              />
              <motion.span
                aria-hidden="true"
                className="absolute w-[400px] h-[400px] rounded-full border border-accent/25"
                animate={{ rotate: -360 }}
                transition={{ duration: 70, repeat: Infinity, ease: "linear" }}
              />
              <motion.img
                src={LOGO.mark}
                alt="FCY IT Consultancy logo"
                className="relative z-10 w-36 sm:w-44 drop-shadow-[0_0_40px_rgba(214,179,106,0.25)]"
                animate={{ y: [-8, 8, -8] }}
                transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
              />

              <motion.div
                initial={{ opacity: 0, x: -14 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.3, duration: 0.6 }}
                className="absolute top-6 left-5 flex items-center gap-2 bg-card border border-border rounded-full px-4 py-2 text-xs text-muted-foreground"
              >
                <BadgeCheck className="w-3.5 h-3.5 text-primary" /> Companies House · No.{" "}
                {COMPANY.number}
              </motion.div>
              <motion.div
                initial={{ opacity: 0, x: 14 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.5, duration: 0.6 }}
                className="absolute bottom-20 right-5 flex items-center gap-2 bg-card border border-border rounded-full px-4 py-2 text-xs text-muted-foreground"
              >
                <MapPin className="w-3.5 h-3.5 text-primary" /> London · Est. 2021
              </motion.div>
              <motion.div
                initial={{ opacity: 0, y: 14 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.7, duration: 0.6 }}
                className="absolute bottom-6 left-5 flex items-center gap-2 bg-card border border-border rounded-full px-4 py-2 text-xs text-muted-foreground"
              >
                <Rocket className="w-3.5 h-3.5 text-primary" /> 7-Day Delivery
              </motion.div>

              <div className="absolute bottom-6 right-5 bg-card border border-border rounded-2xl px-5 py-3.5 shadow-[0_18px_60px_rgba(0,0,0,0.5)] hidden sm:block">
                <p className="text-sm font-semibold text-foreground">{COMPANY.director}</p>
                <p className="text-[10px] uppercase tracking-widest text-primary mt-0.5">
                  Director & Lead Consultant
                </p>
              </div>
            </div>
          </Reveal>
        </div>

        <div className="lg:col-span-7">
          <SectionHeading
            eyebrow="About FCY"
            title="A consultancy built on personal accountability"
          />

          <Reveal delay={0.1}>
            <p className="mt-6 text-base leading-relaxed text-muted-foreground font-editorial">
              FCY IT Consultancy is led personally by{" "}
              <span className="text-foreground font-semibold">Fuatcan Yetimoglu</span> — a
              technologist educated at Bahcesehir University, Istanbul, in Computer Instructional
              and Educational Technology. That teaching background shapes how we work: complex
              technology explained clearly, decisions justified plainly, and clients never left
              in the dark.
            </p>
            <p className="mt-4 text-base leading-relaxed text-muted-foreground font-editorial">
              From a single landing page to a multi-vendor marketplace, from one licence key to a
              company-wide security overhaul — every engagement is handled with the same standard:
              premium quality, honest timelines, and a direct line to the person doing the work.
            </p>
          </Reveal>

          <div className="mt-9 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Reveal delay={0.15} className="card-highlight rounded-2xl bg-card/80 border border-border p-5">
              <UserRound className="w-5 h-5 text-primary mb-3" />
              <h4 className="text-sm font-semibold text-foreground">Director-Led Delivery</h4>
              <p className="text-sm text-muted-foreground mt-1.5 leading-relaxed">
                You deal directly with the director on every project. No account managers, no relay.
              </p>
            </Reveal>
            <Reveal delay={0.2} className="card-highlight rounded-2xl bg-card/80 border border-border p-5">
              <GraduationCap className="w-5 h-5 text-primary mb-3" />
              <h4 className="text-sm font-semibold text-foreground">Educator's Clarity</h4>
              <p className="text-sm text-muted-foreground mt-1.5 leading-relaxed">
                Bahcesehir University, Istanbul — Computer Instructional & Educational Technology.
              </p>
            </Reveal>
          </div>

          <Reveal delay={0.25}>
            <div className="mt-6 card-highlight rounded-2xl bg-card/80 border border-border p-6" data-testid="company-details-panel">
              <div className="flex items-center gap-2 mb-4">
                <Building2 className="w-4 h-4 text-primary" />
                <h4 className="text-sm font-semibold text-foreground uppercase tracking-widest">
                  Company Registration
                </h4>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
                <div>
                  <p className="text-muted-foreground text-xs uppercase tracking-wider">Registered Name</p>
                  <p className="text-foreground mt-0.5">{COMPANY.name}</p>
                </div>
                <div>
                  <p className="text-muted-foreground text-xs uppercase tracking-wider">Company Number</p>
                  <p className="text-foreground mt-0.5 font-mono-meta">{COMPANY.number}</p>
                </div>
                <div className="sm:col-span-2">
                  <p className="text-muted-foreground text-xs uppercase tracking-wider">Registered Office</p>
                  <p className="text-foreground mt-0.5">{COMPANY.address}</p>
                </div>
              </div>
              <Separator className="my-4 bg-border" />
              <div className="flex items-center gap-2 mb-2">
                <FileCheck2 className="w-4 h-4 text-primary" />
                <p className="text-xs uppercase tracking-widest text-muted-foreground">
                  Nature of Business (SIC)
                </p>
              </div>
              <ul className="space-y-1.5">
                {COMPANY.sic.map((sic) => (
                  <li key={sic.code} className="text-sm text-muted-foreground">
                    <span className="font-mono-meta text-primary">{sic.code}</span> — {sic.label}
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
